"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiChevronDown } from "react-icons/fi";
import Container from "@/components/ui/Container";

const faqs = [
  {
    question: "¿Quiénes pueden usar Bloomwise?",
    answer:
      "Está diseñada para agricultores, agrónomos y organizaciones de cualquier tamaño, desde pequeños productores hasta grandes empresas agrícolas.",
  },
  {
    question: "¿Cómo funcionan las sesiones de muestreo?",
    answer:
      "Son campañas de recolección de datos en campo. Se pueden planificar áreas a muestrear, asignar responsables, asociar variables y registrar datos en tiempo real, incluso sin conexión.",
  },
  {
    question: "¿Qué papel cumple la inteligencia artificial en Bloomwise?",
    answer:
      "La IA genera predicciones y recomendaciones personalizadas, ayudando a los usuarios a tomar decisiones informadas y anticipadas sobre el manejo agrícola.",
  },
  {
    question: "¿Qué tan segura es la información en Bloomwise?",
    answer:
      "La plataforma implementa protocolos de seguridad robustos, incluyendo encriptación de datos, backups automáticos y controles de acceso, garantizando la protección y privacidad de toda la información agrícola.",
  },
  {
    question: "¿Cómo gestiona Bloomwise los lotes y campos?",
    answer:
      "Permite crear una estructura jerárquica completa: desde fincas hasta lotes, subdivisiones y surcos individuales. Todo se visualiza en mapas interactivos con integración GIS para una gestión precisa del territorio.",
  },
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section id="faq" className="py-20 bg-white">
      <div className="px-4 md:px-8 lg:px-16 xl:px-[100px]" style={{ maxWidth: "1428px", margin: "0 auto" }}>
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left Column - FAQ Content */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-12 pb-8 border-b-2 border-primary"
            >
              <h2
                className="mb-4 text-[32px] lg:text-[40px] leading-[40px] lg:leading-[50px]"
                style={{
                  alignSelf: "stretch",
                  color: "#242424",
                  fontWeight: 400,
                }}
              >
                ¿Tienes dudas?
              </h2>
              <p
                className="text-[16px] lg:text-[18px] leading-[24px] lg:leading-[28px]"
                style={{
                  alignSelf: "stretch",
                  color: "#242424",
                  fontWeight: 400,
                }}
              >
                Consulta nuestras preguntas frecuentes para saber más sobre las
                funciones, las capacidades de integración y mucho más de
                BloomWise.
              </p>
            </motion.div>

            <div className="space-y-0">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                >
                  <div className="border-b-2 border-primary">
                    <button
                      onClick={() =>
                        setOpenIndex(openIndex === index ? null : index)
                      }
                      className="w-full py-6 flex items-center justify-between text-left group"
                    >
                      <span
                        className="font-semibold text-gray-900 pr-8 text-[18px] lg:text-[20px] leading-[24px] lg:leading-[26px]"
                        style={{
                          fontWeight: 600,
                        }}
                      >
                        {faq.question}
                      </span>
                      <motion.div
                        animate={{ rotate: openIndex === index ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                        className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                          openIndex === index ? 'bg-accent' : 'bg-primary'
                        }`}
                      >
                        <FiChevronDown className="w-5 h-5 text-white" />
                      </motion.div>
                    </button>

                    <AnimatePresence>
                      {openIndex === index && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <div
                            className="pb-6 text-gray-600 text-[16px] lg:text-[18px] leading-[22px] lg:leading-[24px]"
                          >
                            {faq.answer}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Right Column - Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            style={{
              flex: "1 0 0",
              alignSelf: "stretch",
              background:
                "url('/images/Bloom-Wise_imagen_hand-with-plant.webp') lightgray 50% / cover no-repeat",
            }}
          ></motion.div>
        </div>
      </div>
    </section>
  );
}
